<script setup>
import { ref, watch, computed } from 'vue'
import axios from 'axios'
import { useProjectionsStore } from '../stores/projections'

const projectionsStore = useProjectionsStore()

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  fixtureId: {
    type: [String, Number],
    default: null
  },
  playerId: {
    type: [String, Number],
    default: null
  },
  market: {
    type: String,
    default: ''
  },
  playerName: {
    type: String,
    default: ''
  },
  marketLabel: {
    type: String,
    default: ''
  },
  /** When set, render as panel below cell instead of overlay */
  cellRect: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close'])

const expansionLogData = ref(null)
const expansionLogLoading = ref(false)

const fetchExpansionLog = async () => {
  const { fixtureId, playerId, market } = props
  if (!fixtureId || !playerId || !market) return

  expansionLogLoading.value = true
  expansionLogData.value = null
  try {
    const { data } = await axios.get('/api/balanced-lines/expansion-log', {
      params: { fixture_id: fixtureId, player_id: playerId, market }
    })
    expansionLogData.value = data?.auto_expanded_market_settings_parameters ?? null
  } catch (err) {
    console.error('Failed to fetch expansion log:', err)
    expansionLogData.value = null
  } finally {
    expansionLogLoading.value = false
  }
}

watch(() => props.isOpen, (open) => {
  if (open && props.fixtureId && props.playerId && props.market) {
    fetchExpansionLog()
  } else {
    expansionLogData.value = null
  }
})

// Debug data from store (Comparable Data Stats, Minutes, SQL, ClickHouse)
const debugItems = computed(() =>
  projectionsStore.debug_player_market_data.filter(
    item => String(item.market_id) === String(props.market) && String(item.player_id) === String(props.playerId)
  )
)

const close = () => emit('close')
</script>

<template>
  <!-- Panel mode: below cell, no overlay -->
  <div
    v-if="isOpen && cellRect"
    class="expansion-log-panel fixed z-[100] bg-white rounded-lg shadow-xl border border-gray-200 overflow-y-auto"
    :style="{
      top: `${cellRect.bottom + 4}px`,
      left: `${cellRect.left}px`,
      width: `${Math.max(cellRect.width, 900)}px`,
      maxHeight: 'min(80vh, 580px)'
    }"
    @click.stop
  >
    <div class="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
      <div>
        <h3 class="text-sm font-semibold text-gray-800">
          Debug — {{ marketLabel || market }} · {{ playerName || 'Player' }}
        </h3>
      </div>
      <button
        @click="close"
        class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
      >
        <i class="fas fa-times text-lg" />
      </button>
    </div>
    <div class="p-4 space-y-4">
      <!-- Comparable Data Stats, Minutes Analysis, SQL, ClickHouse (from store) -->
      <div v-if="debugItems.length === 0" class="text-gray-500 text-sm py-2">
        No comparable debug data. Load comparable data (click arrows on cell) first.
      </div>
      <div v-for="item in debugItems" :key="item.player_id" class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h4 class="text-sm font-medium text-gray-700 mb-2 border-b border-gray-200 pb-2">Comparable Data Stats</h4>
            <div class="space-y-2">
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Count:</span>
                <span class="font-medium">{{ item.debug.count }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Per 30:</span>
                <span class="font-medium">{{ item.debug.per30 }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Avg Per 30:</span>
                <span class="font-medium">{{ item.debug.comparableDataAveragePerThrity }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Median Per 30:</span>
                <span class="font-medium">{{ item.debug.comparableDataMedianPerThrity }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Market Projection:</span>
                <span class="font-medium">{{ item.debug.marketProjection }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Market Average:</span>
                <span class="font-medium">{{ item.debug.comparableDataMarketAverage }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Projection − Avg (diff):</span>
                <span class="font-medium font-mono">{{ item.debug.projectionVsMarketAverageDiff != null ? item.debug.projectionVsMarketAverageDiff : '—' }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Market Median:</span>
                <span class="font-medium">{{ item.debug.comparableDataMarketMedian }}</span>
              </div>
            </div>
          </div>
          <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <h4 class="text-sm font-medium text-gray-700 mb-2 border-b border-gray-200 pb-2">Minutes Analysis</h4>
            <div class="space-y-2">
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Projected Minutes:</span>
                <span class="font-medium">{{ item.debug.minute }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Avg Minutes Per Game:</span>
                <span class="font-medium">{{ item.debug.comparableDataAvgMinutesPerGame }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Median Minutes Per Game:</span>
                <span class="font-medium">{{ item.debug.comparableDataMedianMinutesPerGame }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Avg Actual Minutes:</span>
                <span class="font-medium">{{ item.debug.comparableDataAvgMinutes }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Median Actual Minutes:</span>
                <span class="font-medium">{{ item.debug.comparableDataMedianMinutes }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h4 class="text-sm font-medium text-gray-700 mb-2">SQL Query</h4>
          <div class="bg-gray-100 p-3 rounded text-xs font-mono overflow-x-auto">
            <div class="text-gray-600 mb-1">Raw SQL:</div>
            <div class="mb-2 whitespace-pre-wrap">{{ item.debug.raw_sql }}</div>
            <div class="text-gray-600 mb-1">SQL with Bindings:</div>
            <div class="mb-2 whitespace-pre-wrap">{{ item.debug.sql_with_bindings }}</div>
            <div class="text-gray-600 mb-1">Bindings:</div>
            <div>{{ item.debug?.bindings?.join(', ') }}</div>
          </div>
        </div>
        <div v-if="item.debug?.clickhouse_query" class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h4 class="text-sm font-medium text-gray-700 mb-2">ClickHouse Query</h4>
          <div class="bg-gray-100 p-3 rounded text-xs font-mono overflow-x-auto">
            <div class="text-gray-600 mb-1">ClickHouse Query:</div>
            <div class="whitespace-pre-wrap">{{ item.debug.clickhouse_query }}</div>
          </div>
        </div>
      </div>

      <!-- Expansion Log (fetched from API) -->
      <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <h4 class="text-sm font-medium text-gray-700 mb-3 border-b border-gray-200 pb-2">
          Auto-expanded Market Settings (ClickHouse)
        </h4>
        <div v-if="expansionLogLoading" class="text-gray-500 text-sm py-8 text-center">Loading...</div>
        <div v-else-if="!expansionLogData" class="text-gray-500 text-sm py-8 text-center">
          No expansion log data. Load comparable data first to populate.
        </div>
        <template v-else>
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div class="space-y-2">
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Market:</span>
                <span class="font-medium">{{ expansionLogData.market }}</span>
              </div>
              <div v-if="expansionLogData.session_id" class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Session ID:</span>
                <span class="font-mono text-xs truncate max-w-[180px]" :title="expansionLogData.session_id">
                  {{ expansionLogData.session_id }}
                </span>
              </div>
              <div v-if="expansionLogData.created_at" class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Created At:</span>
                <span class="font-medium">{{ expansionLogData.created_at }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Expansion Step:</span>
                <span class="font-medium">per30: {{ expansionLogData.expansion_step?.per30 }}, minutes: {{ expansionLogData.expansion_step?.minutes }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Iterations:</span>
                <span class="font-medium">{{ expansionLogData.total_iterations_used }} / {{ expansionLogData.max_iterations }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">Threshold:</span>
                <span class="font-medium">{{ expansionLogData.threshold }}</span>
              </div>
            </div>
            <div v-if="expansionLogData.three_point_field_goal_expansion_step != null" class="space-y-2">
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">3PT Expansion Step:</span>
                <span class="font-medium">{{ expansionLogData.three_point_field_goal_expansion_step }}</span>
              </div>
              <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                <span class="text-gray-600">3PT Final Range:</span>
                <span class="font-medium">{{ expansionLogData.three_point_field_goal_final_start }} – {{ expansionLogData.three_point_field_goal_final_end }}</span>
              </div>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full text-xs border-collapse border border-gray-200">
              <thead>
                <tr class="bg-gray-50">
                  <th class="border border-gray-200 px-2 py-1 text-left">Iteration</th>
                  <th class="border border-gray-200 px-2 py-1 text-left">Per30 Start</th>
                  <th class="border border-gray-200 px-2 py-1 text-left">Per30 End</th>
                  <th class="border border-gray-200 px-2 py-1 text-left">Avg Min Start</th>
                  <th class="border border-gray-200 px-2 py-1 text-left">Avg Min End</th>
                  <th class="border border-gray-200 px-2 py-1 text-left">Result Count</th>
                  <th class="border border-gray-200 px-2 py-1 text-left">Met</th>
                  <th v-if="expansionLogData.requests?.[0]?.three_point_per30_start != null" class="border border-gray-200 px-2 py-1 text-left">3PT Start</th>
                  <th v-if="expansionLogData.requests?.[0]?.three_point_per30_end != null" class="border border-gray-200 px-2 py-1 text-left">3PT End</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="req in expansionLogData.requests" :key="req.iteration" :class="req.met_threshold ? 'bg-green-50' : ''">
                  <td class="border border-gray-200 px-2 py-1">
                    <span v-if="req.iteration === 0" class="text-gray-600">0 <span class="text-gray-400">(Initial)</span></span>
                    <span v-else>{{ req.iteration }} <span class="text-gray-400">(Expanded)</span></span>
                  </td>
                  <td class="border border-gray-200 px-2 py-1">{{ req.per30_start }}</td>
                  <td class="border border-gray-200 px-2 py-1">{{ req.per30_end }}</td>
                  <td class="border border-gray-200 px-2 py-1">{{ req.avg_minutes_start }}</td>
                  <td class="border border-gray-200 px-2 py-1">{{ req.avg_minutes_end }}</td>
                  <td class="border border-gray-200 px-2 py-1">{{ req.result_count }}</td>
                  <td class="border border-gray-200 px-2 py-1">{{ req.met_threshold ? 'Yes' : 'No' }}</td>
                  <td v-if="req.three_point_per30_start != null" class="border border-gray-200 px-2 py-1">{{ req.three_point_per30_start }}</td>
                  <td v-if="req.three_point_per30_end != null" class="border border-gray-200 px-2 py-1">{{ req.three_point_per30_end }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
  </div>

  <!-- Overlay mode (fallback) -->
  <div v-else-if="isOpen" class="fixed inset-0 z-[100] overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="fixed inset-0 bg-black/50" @click="close" />
      <div class="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div class="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
          <div>
            <h3 class="text-lg font-semibold text-gray-800">
              Debug — {{ marketLabel || market }} · {{ playerName || 'Player' }}
            </h3>
            <p class="text-xs text-gray-500 mt-0.5">Right-click market cell or click debug icon to open</p>
          </div>
          <button
            @click="close"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <i class="fas fa-times text-lg" />
          </button>
        </div>
        <div class="p-4 space-y-4">
          <!-- Comparable Data Stats, Minutes Analysis, SQL, ClickHouse (from store) -->
          <div v-if="debugItems.length === 0" class="text-gray-500 text-sm py-2">
            No comparable debug data. Load comparable data (click arrows on cell) first.
          </div>
          <div v-for="item in debugItems" :key="item.player_id" class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <h4 class="text-sm font-medium text-gray-700 mb-2 border-b border-gray-200 pb-2">Comparable Data Stats</h4>
                <div class="space-y-2">
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Count:</span>
                    <span class="font-medium">{{ item.debug.count }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Per 30:</span>
                    <span class="font-medium">{{ item.debug.per30 }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Avg Per 30:</span>
                    <span class="font-medium">{{ item.debug.comparableDataAveragePerThrity }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Median Per 30:</span>
                    <span class="font-medium">{{ item.debug.comparableDataMedianPerThrity }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Market Projection:</span>
                    <span class="font-medium">{{ item.debug.marketProjection }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Market Average:</span>
                    <span class="font-medium">{{ item.debug.comparableDataMarketAverage }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Projection − Avg (diff):</span>
                    <span class="font-medium font-mono">{{ item.debug.projectionVsMarketAverageDiff != null ? item.debug.projectionVsMarketAverageDiff : '—' }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Market Median:</span>
                    <span class="font-medium">{{ item.debug.comparableDataMarketMedian }}</span>
                  </div>
                </div>
              </div>
              <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <h4 class="text-sm font-medium text-gray-700 mb-2 border-b border-gray-200 pb-2">Minutes Analysis</h4>
                <div class="space-y-2">
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Projected Minutes:</span>
                    <span class="font-medium">{{ item.debug.minute }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Avg Minutes Per Game:</span>
                    <span class="font-medium">{{ item.debug.comparableDataAvgMinutesPerGame }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Median Minutes Per Game:</span>
                    <span class="font-medium">{{ item.debug.comparableDataMedianMinutesPerGame }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Avg Actual Minutes:</span>
                    <span class="font-medium">{{ item.debug.comparableDataAvgMinutes }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Median Actual Minutes:</span>
                    <span class="font-medium">{{ item.debug.comparableDataMedianMinutes }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h4 class="text-sm font-medium text-gray-700 mb-2">SQL Query</h4>
              <div class="bg-gray-100 p-3 rounded text-xs font-mono overflow-x-auto">
                <div class="text-gray-600 mb-1">Raw SQL:</div>
                <div class="mb-2 whitespace-pre-wrap">{{ item.debug.raw_sql }}</div>
                <div class="text-gray-600 mb-1">SQL with Bindings:</div>
                <div class="mb-2 whitespace-pre-wrap">{{ item.debug.sql_with_bindings }}</div>
                <div class="text-gray-600 mb-1">Bindings:</div>
                <div>{{ item.debug?.bindings?.join(', ') }}</div>
              </div>
            </div>
            <div v-if="item.debug?.clickhouse_query" class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
              <h4 class="text-sm font-medium text-gray-700 mb-2">ClickHouse Query</h4>
              <div class="bg-gray-100 p-3 rounded text-xs font-mono overflow-x-auto">
                <div class="text-gray-600 mb-1">ClickHouse Query:</div>
                <div class="whitespace-pre-wrap">{{ item.debug.clickhouse_query }}</div>
              </div>
            </div>
          </div>

          <!-- Expansion Log (fetched from API) -->
          <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 class="text-sm font-medium text-gray-700 mb-3 border-b border-gray-200 pb-2">
              Auto-expanded Market Settings (ClickHouse)
            </h4>
            <div v-if="expansionLogLoading" class="text-gray-500 text-sm py-8 text-center">Loading...</div>
            <div v-else-if="!expansionLogData" class="text-gray-500 text-sm py-8 text-center">
              No expansion log data. Load comparable data first to populate.
            </div>
            <template v-else>
              <div class="grid grid-cols-2 gap-4 mb-4">
                <div class="space-y-2">
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Market:</span>
                    <span class="font-medium">{{ expansionLogData.market }}</span>
                  </div>
                  <div v-if="expansionLogData.session_id" class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Session ID:</span>
                    <span class="font-mono text-xs truncate max-w-[180px]" :title="expansionLogData.session_id">
                      {{ expansionLogData.session_id }}
                    </span>
                  </div>
                  <div v-if="expansionLogData.created_at" class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Created At:</span>
                    <span class="font-medium">{{ expansionLogData.created_at }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Expansion Step:</span>
                    <span class="font-medium">per30: {{ expansionLogData.expansion_step?.per30 }}, minutes: {{ expansionLogData.expansion_step?.minutes }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Iterations:</span>
                    <span class="font-medium">{{ expansionLogData.total_iterations_used }} / {{ expansionLogData.max_iterations }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">Threshold:</span>
                    <span class="font-medium">{{ expansionLogData.threshold }}</span>
                  </div>
                </div>
                <div v-if="expansionLogData.three_point_field_goal_expansion_step != null" class="space-y-2">
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">3PT Expansion Step:</span>
                    <span class="font-medium">{{ expansionLogData.three_point_field_goal_expansion_step }}</span>
                  </div>
                  <div class="flex justify-between border-b border-gray-100 pb-1 text-sm">
                    <span class="text-gray-600">3PT Final Range:</span>
                    <span class="font-medium">{{ expansionLogData.three_point_field_goal_final_start }} – {{ expansionLogData.three_point_field_goal_final_end }}</span>
                  </div>
                </div>
              </div>
              <div class="overflow-x-auto">
                <table class="min-w-full text-xs border-collapse border border-gray-200">
                  <thead>
                    <tr class="bg-gray-50">
                      <th class="border border-gray-200 px-2 py-1 text-left">Iteration</th>
                      <th class="border border-gray-200 px-2 py-1 text-left">Per30 Start</th>
                      <th class="border border-gray-200 px-2 py-1 text-left">Per30 End</th>
                      <th class="border border-gray-200 px-2 py-1 text-left">Avg Min Start</th>
                      <th class="border border-gray-200 px-2 py-1 text-left">Avg Min End</th>
                      <th class="border border-gray-200 px-2 py-1 text-left">Result Count</th>
                      <th class="border border-gray-200 px-2 py-1 text-left">Met</th>
                      <th v-if="expansionLogData.requests?.[0]?.three_point_per30_start != null" class="border border-gray-200 px-2 py-1 text-left">3PT Start</th>
                      <th v-if="expansionLogData.requests?.[0]?.three_point_per30_end != null" class="border border-gray-200 px-2 py-1 text-left">3PT End</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="req in expansionLogData.requests" :key="req.iteration" :class="req.met_threshold ? 'bg-green-50' : ''">
                      <td class="border border-gray-200 px-2 py-1">
                        <span v-if="req.iteration === 0" class="text-gray-600">0 <span class="text-gray-400">(Initial)</span></span>
                        <span v-else>{{ req.iteration }} <span class="text-gray-400">(Expanded)</span></span>
                      </td>
                      <td class="border border-gray-200 px-2 py-1">{{ req.per30_start }}</td>
                      <td class="border border-gray-200 px-2 py-1">{{ req.per30_end }}</td>
                      <td class="border border-gray-200 px-2 py-1">{{ req.avg_minutes_start }}</td>
                      <td class="border border-gray-200 px-2 py-1">{{ req.avg_minutes_end }}</td>
                      <td class="border border-gray-200 px-2 py-1">{{ req.result_count }}</td>
                      <td class="border border-gray-200 px-2 py-1">{{ req.met_threshold ? 'Yes' : 'No' }}</td>
                      <td v-if="req.three_point_per30_start != null" class="border border-gray-200 px-2 py-1">{{ req.three_point_per30_start }}</td>
                      <td v-if="req.three_point_per30_end != null" class="border border-gray-200 px-2 py-1">{{ req.three_point_per30_end }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
